import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { ClientCustomerListItemType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useGlobal } from 'context/GlobalContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import SvgTrash from 'components/svg/SvgTrash';
import { useCustomersList } from '../context/CustomersListProvider';

const buildCustomersListOrder = (order: string, desc: boolean) =>
    `${order ? `order by ${order} ` : ''}${desc ? 'DESC' : ''}`;

const useCustomersListPage = () => {
    const { t } = useTranslation(['Clients', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const {
        showSpinner,
        setShowSpinner,
        customersOrder,
        customersDesc,
        filterClientId,
        filterVatEnabled,
        setCustomersOrder,
        setCustomersDesc,
        setFilterClientId,
        setFilterVatEnabled,
        listResetNonce,
        listRefreshNonce,
    } = useCustomersList();

    const [list, setList] = useState<ClientCustomerListItemType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [editCustomer, setEditCustomer] = useState<ClientCustomerListItemType | null>(null);
    const [isCreate, setIsCreate] = useState(false);
    const [formNonce, setFormNonce] = useState(0);

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchList = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return;

        const listData = {
            search,
            order: buildCustomersListOrder(customersOrder, customersDesc),
            page: page < 0 ? 1 : page || 1,
            onPage,
            clientId: filterClientId,
            vatEnabled: filterVatEnabled,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const res = await apiClient.getCustomersList(listData);
        setShowSpinner(false);

        if (!res) return showToast(t('common:messError'), 'danger');

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));

        setList(res.customers);
    };

    const resetList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setCustomersOrder('');
        setCustomersDesc(false);
        setFilterClientId(null);
        setFilterVatEnabled(null);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
            clientId: null as number | null,
            vatEnabled: null as 0 | 1 | null,
        };

        setShowSpinner(true);
        const res = await apiClient.getCustomersList(listData);
        setShowSpinner(false);

        if (!res) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));

        setList(res.customers);
        skipFetchOnStateChangeRef.current = false;
    }, [
        g,
        location.pathname,
        onPage,
        setCustomersDesc,
        setCustomersOrder,
        setFilterClientId,
        setFilterVatEnabled,
        setShowSpinner,
        showToast,
        t,
    ]);

    const handleHideEdit = () => {
        setIsCreate(false);
        setEditCustomer(null);
    };

    const handleCreate = () => {
        setEditCustomer(null);
        setFormNonce((n) => n + 1);
        setIsCreate(true);
    };

    const handleEdit = (row: ClientCustomerListItemType) => {
        setIsCreate(false);
        setFormNonce((n) => n + 1);
        setEditCustomer(row);
    };

    const handleSaved = async () => {
        setShowSpinner(true);
        await fetchList(true);
    };

    const handleDelete = (row: ClientCustomerListItemType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            setShowSpinner(true);
            const stat = await apiClient.deleteClientCustomer(row.clientId, row.ID);
            await fetchList(true);
            setShowSpinner(false);
            if (stat) showToast(t('subjects.messDeleted'), 'success');
            else showToast(t('common:messError'), 'danger');
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('subjects.confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{row.fullName || row.edrpouOrIpn}</h5>
                    <small className="form-text text-muted">{row.edrpouOrIpn}</small>
                </>
            ),
        });
    };

    useListRefreshEffect(listResetNonce, () => {
        void resetList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchList(true);
    });

    useEffect(() => {
        if (page === 1) fetchList(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchList();
    }, [page, filterClientId, filterVatEnabled, customersOrder, customersDesc, onPage]);

    useEffect(() => {
        const nextPage = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== nextPage) setPage(nextPage);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    return {
        list,
        editCustomer,
        isCreate,
        formNonce,
        handleHideEdit,
        handleCreate,
        handleEdit,
        handleDelete,
        handleSaved,
    };
};

export default useCustomersListPage;
